import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { catchError, firstValueFrom, map, of, throwError } from 'rxjs';
import { AbrirAtendimentoRequest } from '../../interfaces/abrir-atendimento-request';
import { AbrirAtendimentoResponse } from '../../interfaces/abrir-atendimento-response';
import { Convenio } from '../../interfaces/convenio';
import { CategoriaConvenio } from '../../interfaces/categoria-convenio';
import { SetorAtendimento } from '../../interfaces/setor-atendimento';
import { ProcedimentoBusca, ProcedimentoClassificacao, FiltroTipoProcedimento } from '../../interfaces/procedimento-busca';
import { OrcamentoItem } from '../../interfaces/orcamento-item';
import { AtendimentoDuplicadoResult } from '../../interfaces/atendimento-duplicado-result';
import { ClinicaParceira } from '../../interfaces/clinica-parceira';
import { DadosPagamento, ProcessarPagamentoRequest, ProcessarPagamentoResult, ItemContaPaciente, CupomDesconto, MotivoDesconto, AplicarDescontoManualResult, PacienteBusca, ContaPacienteResumo, AdiantamentoDisponivel, ResumoConclusao, FichaAtendimento, MeuAtendimento } from '../../interfaces/dados-pagamento';

export interface AcaoContaResultado {
  nrInternoConta: number;
  mensagem: string;
  cuponsNaoReativados?: string[];
}

export interface CupomAplicadoConta {
  nrSeqCupomPaciente: number;
  codCupom: string;
  dsCupom?: string;
}

export interface MotivoExclusaoConta {
  tipo: 'duplicidade' | 'outro' | 'rapido';
  justificativa?: string;
  contaRef?: number;
}

export interface ContextoCatalogo {
  nrSeqAgendamento?: number | null;
  nrAtendimento?: number | null;
  /** Agenda de clínica parceira escolhida sem agendamento (Super). */
  cdAgenda?: number | null;
}

export interface ContextoConvenios extends ContextoCatalogo {
  cdEstabelecimento?: number | null;
}

function paramsDe(valores: Record<string, string | number | null | undefined>): HttpParams {
  let params = new HttpParams();
  for (const [k, v] of Object.entries(valores)) {
    if (v != null && v !== '') params = params.set(k, String(v));
  }
  return params;
}

export class AcaoContaErro extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
  }
}

@Injectable({
  providedIn: 'root',
})
export class AtendimentoService {
  private apiURL = environment.apiUrl + '/v' + environment.apiVersion;

  private conveniosCache = new Map<string, Convenio[]>();

  constructor(private httpClient: HttpClient) {}

  public async abrirAtendimentoCompleto(
    request: AbrirAtendimentoRequest,
  ): Promise<AbrirAtendimentoResponse> {
    const response = await firstValueFrom(
      this.httpClient
        .post<any>(`${this.apiURL}/Atendimento/abrir-completo`, request)
        .pipe(catchError(this.handleError)),
    );

    if (!response?.data?.length) {
      throw new Error('Atendimento não retornado pela API.');
    }

    return response.data[0] as AbrirAtendimentoResponse;
  }

  public async buscarProcedimentos(
    termo: string,
    opcoes?: { filtro?: FiltroTipoProcedimento; cdConvenio?: number | null; cdCategoria?: number | null; cdEstabelecimento?: number | null; nrSeqAgendamento?: number | null; nrAtendimento?: number | null; cdAgenda?: number | null },
  ): Promise<ProcedimentoBusca[]> {
    const params = paramsDe({
      termo,
      filtro: opcoes?.filtro && opcoes.filtro !== 'todos' ? opcoes.filtro : null,
      cdConvenio: opcoes?.cdConvenio,
      cdCategoria: opcoes?.cdCategoria,
      cdEstabelecimento: opcoes?.cdEstabelecimento,
      nrSeqAgendamento: opcoes?.nrSeqAgendamento,
      nrAtendimento: opcoes?.nrAtendimento,
      cdAgenda: opcoes?.cdAgenda,
    });

    const response = await firstValueFrom(
      this.httpClient.get<any>(`${this.apiURL}/Atendimento/buscar-procedimentos`, { params }).pipe(catchError(this.handleError)),
    );
    if (!response?.data?.length) return [];
    return response.data[0] as ProcedimentoBusca[];
  }

  public async getTabelaClassificacoes(
    cdEstabelecimento: number, cdConvenio?: number | null, cdCategoria?: number | null, ctx?: ContextoCatalogo,
  ): Promise<ProcedimentoClassificacao[]> {
    const params = paramsDe({ cdEstabelecimento, cdConvenio, cdCategoria, nrSeqAgendamento: ctx?.nrSeqAgendamento, nrAtendimento: ctx?.nrAtendimento, cdAgenda: ctx?.cdAgenda });

    const response = await firstValueFrom(
      this.httpClient.get<any>(`${this.apiURL}/Atendimento/tabela-procedimentos/classificacoes`, { params }).pipe(catchError(this.handleError)),
    );
    if (!response?.data?.length) return [];
    return response.data[0] as ProcedimentoClassificacao[];
  }

  public async getTabelaProcedimentos(
    cdEstabelecimento: number,
    filtros: { nrSeqClassif?: number | null; termo?: string | null; cdConvenio?: number | null; cdCategoria?: number | null },
    ctx?: ContextoCatalogo,
  ): Promise<ProcedimentoBusca[]> {
    const params = paramsDe({
      cdEstabelecimento,
      nrSeqClassif: filtros.nrSeqClassif,
      termo: filtros.termo,
      cdConvenio: filtros.cdConvenio,
      cdCategoria: filtros.cdCategoria,
      nrSeqAgendamento: ctx?.nrSeqAgendamento,
      nrAtendimento: ctx?.nrAtendimento,
      cdAgenda: ctx?.cdAgenda,
    });

    const response = await firstValueFrom(
      this.httpClient.get<any>(`${this.apiURL}/Atendimento/tabela-procedimentos`, { params }).pipe(catchError(this.handleError)),
    );
    if (!response?.data?.length) return [];
    return response.data[0] as ProcedimentoBusca[];
  }

  public async getProcedimentoPadrao(
    nrSeqAgendamento: number,
    cdEspecialidade: number,
    tipoAgenda: string = 'CONSULTATION',
    isRetorno: boolean = false,
    cdEstabelecimento?: number | null,
  ): Promise<ProcedimentoBusca | null> {
    try {
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(`${this.apiURL}/Atendimento/procedimento-padrao`, {
            params: paramsDe({ nrSeqAgendamento, cdEspecialidade, tipoAgenda, isRetorno: String(isRetorno), cdEstabelecimento }),
          })
          .pipe(
            catchError((err: HttpErrorResponse) => {
              // 404 = sem procedimento padrão (comportamento esperado)
              if (err.status === 404) return of(null);
              return this.handleError(err);
            }),
          ),
      );
      if (!response?.data?.length) return null;
      return response.data[0] as ProcedimentoBusca;
    } catch {
      return null;
    }
  }

  public async getOrcamentos(cdPessoaFisica: number): Promise<OrcamentoItem[]> {
    try {
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(`${this.apiURL}/Atendimento/orcamentos/${cdPessoaFisica}`)
          .pipe(catchError(this.handleError)),
      );
      if (!response?.data?.length) return [];
      return response.data[0] as OrcamentoItem[];
    } catch {
      return [];
    }
  }

  public async getConvenios(ctx?: ContextoConvenios): Promise<Convenio[]> {
    const chave = `${ctx?.cdEstabelecimento ?? ''}|${ctx?.nrSeqAgendamento ?? ''}|${ctx?.nrAtendimento ?? ''}|${ctx?.cdAgenda ?? ''}`;
    const emCache = this.conveniosCache.get(chave);
    if (emCache) return emCache;

    const params = paramsDe({
      cdEstabelecimento: ctx?.cdEstabelecimento,
      nrSeqAgendamento: ctx?.nrSeqAgendamento,
      nrAtendimento: ctx?.nrAtendimento,
      cdAgenda: ctx?.cdAgenda,
    });
    const response = await firstValueFrom(
      this.httpClient
        .get<any>(`${this.apiURL}/Atendimento/convenios`, { params })
        .pipe(catchError(this.handleError)),
    );
    const lista = (response?.data?.[0] ?? []) as Convenio[];
    if (lista.length) this.conveniosCache.set(chave, lista);
    return lista;
  }

  /** Agendas de parceiro para abrir sem agendamento; vazia fora da Super. Falha vira lista vazia. */
  public async getClinicasParceiras(cdEstabelecimento: number): Promise<ClinicaParceira[]> {
    try {
      const response = await firstValueFrom(
        this.httpClient.get<any>(`${this.apiURL}/Atendimento/clinicas-parceiras`, { params: paramsDe({ cdEstabelecimento }) }),
      );
      return (response?.data ?? []) as ClinicaParceira[];
    } catch {
      return [];
    }
  }

  public async getCategorias(cdConvenio: number, cdEstabelecimento?: number | null): Promise<CategoriaConvenio[]> {
    const response = await firstValueFrom(
      this.httpClient
        .get<any>(`${this.apiURL}/Atendimento/categorias/${cdConvenio}`, { params: paramsDe({ cdEstabelecimento }) })
        .pipe(catchError(this.handleError)),
    );
    if (!response?.data?.length) return [];
    return response.data[0] as CategoriaConvenio[];
  }

  public async verificarAtendimentoDuplicado(
    cdPessoaFisica: number,
    cdEstabelecimento: number,
    cdMedico: number,
  ): Promise<AtendimentoDuplicadoResult> {
    try {
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(
            `${this.apiURL}/Atendimento/verificar-duplicado?cdPessoaFisica=${cdPessoaFisica}&cdEstabelecimento=${cdEstabelecimento}&cdMedico=${cdMedico}`,
          )
          .pipe(catchError(this.handleError)),
      );
      if (!response?.data?.length) return { existe: false, nrAtendimento: null, dtAtendimento: null };
      return response.data[0] as AtendimentoDuplicadoResult;
    } catch {
      return { existe: false, nrAtendimento: null, dtAtendimento: null };
    }
  }

  public async getSetores(cdEstabelecimento: number): Promise<SetorAtendimento[]> {
    const response = await firstValueFrom(
      this.httpClient
        .get<any>(`${this.apiURL}/Atendimento/setores/${cdEstabelecimento}`)
        .pipe(catchError(this.handleError)),
    );

    if (!response?.data?.length) return [];

    return response.data[0] as SetorAtendimento[];
  }

  public async getDadosPagamento(nrAtendimento: number, cdPessoaFisica: number): Promise<DadosPagamento | null> {
    try {
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(`${this.apiURL}/Atendimento/dados-pagamento/${nrAtendimento}?cdPessoaFisica=${cdPessoaFisica}`)
          .pipe(catchError(this.handleError)),
      );
      if (!response?.data?.length) return null;
      return response.data[0] as DadosPagamento;
    } catch {
      return null;
    }
  }

  public async processarPagamento(request: ProcessarPagamentoRequest): Promise<ProcessarPagamentoResult> {
    const response = await firstValueFrom(
      this.httpClient
        .post<any>(`${this.apiURL}/Atendimento/processar-pagamento`, request)
        .pipe(catchError(this.handleError)),
    );
    if (!response?.data?.length) {
      throw new Error('Pagamento não retornado pela API.');
    }
    return response.data[0] as ProcessarPagamentoResult;
  }

  public async alterarQuantidadeItem(nrSequencia: number, qtProcedimento: number, nrInternoConta: number, nmUsuario: string): Promise<void> {
    await firstValueFrom(
      this.httpClient
        .put<any>(`${this.apiURL}/Atendimento/item-conta/quantidade`, { nrSequencia, qtProcedimento, nrInternoConta, nmUsuario })
        .pipe(catchError(this.handleError)),
    );
  }

  public async alterarMedicoExecutor(nrSequencia: number, cdMedicoExecutor: number, nmUsuario: string): Promise<string> {
    const response = await firstValueFrom(
      this.httpClient
        .patch<any>(`${this.apiURL}/Atendimento/item-conta/${nrSequencia}/medico-executor`, { cdMedicoExecutor, nmUsuario })
        .pipe(catchError(this.handleError)),
    );
    return response?.data?.[0]?.nmMedico ?? '';
  }

  public async getCuponsDisponiveis(cdPessoaFisica: number): Promise<CupomDesconto[]> {
    try {
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(`${this.apiURL}/Atendimento/cupons/${cdPessoaFisica}`)
          .pipe(catchError(this.handleError)),
      );
      if (!response?.data?.length) return [];
      return response.data[0] as CupomDesconto[];
    } catch {
      return [];
    }
  }

  public async aplicarCupom(request: { codCupom: number; nrAtendimento: number; nrInternoConta: number; nmUsuario: string }): Promise<void> {
    await firstValueFrom(
      this.httpClient
        .post<any>(`${this.apiURL}/Atendimento/aplicar-cupom`, request)
        .pipe(catchError(this.handleError)),
    );
  }

  public async getMotivosDesconto(): Promise<MotivoDesconto[]> {
    try {
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(`${this.apiURL}/Atendimento/motivos-desconto`)
          .pipe(catchError(this.handleError)),
      );
      if (!response?.data?.length) return [];
      return response.data[0] as MotivoDesconto[];
    } catch {
      return [];
    }
  }

  public async aplicarDescontoPep(request: { nrInternoConta: number; percentual: number; observacao?: string; nrSeqMotivoDesc: number; nmUsuario: string }): Promise<AplicarDescontoManualResult> {
    const response = await firstValueFrom(
      this.httpClient
        .post<any>(`${this.apiURL}/Atendimento/aplicar-desconto-pep`, request)
        .pipe(catchError(this.handleError)),
    );
    if (!response?.data?.length) throw new Error('Desconto não retornado pela API.');
    return response.data[0] as AplicarDescontoManualResult;
  }

  public async cobrarMaquineta(request: { metodo: number; valor: number; parcelas: number; terminal: string; descricao: string; patientId: number; cdEstabelecimento: number; isAvulsoFila?: boolean }): Promise<any> {
    const response = await firstValueFrom(
      this.httpClient
        .post<any>(`${this.apiURL}/Atendimento/cobrar-maquineta`, request)
        .pipe(catchError(this.handleError)),
    );
    return response;
  }

  public async adicionarProcedimentoConta(request: any): Promise<void> {
    await firstValueFrom(
      this.httpClient
        .post<any>(`${this.apiURL}/Atendimento/adicionar-procedimento-conta`, request)
        .pipe(catchError(this.handleError)),
    );
  }

  public async getItensContaPaciente(nrInternoConta: number): Promise<ItemContaPaciente[]> {
    try {
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(`${this.apiURL}/Atendimento/itens-conta/${nrInternoConta}`)
          .pipe(catchError(this.handleError)),
      );
      if (!response?.data?.length) return [];
      return response.data[0] as ItemContaPaciente[];
    } catch {
      return [];
    }
  }

  public async buscarPaciente(termo: string): Promise<PacienteBusca[]> {
    try {
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(`${this.apiURL}/Atendimento/buscar-paciente?q=${encodeURIComponent(termo)}`)
          .pipe(catchError(this.handleError)),
      );
      if (!response?.data?.length) return [];
      return response.data[0] as PacienteBusca[];
    } catch {
      return [];
    }
  }

  public async getContasPaciente(cdPessoaFisica: number): Promise<ContaPacienteResumo[]> {
    try {
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(`${this.apiURL}/Atendimento/contas-paciente/${cdPessoaFisica}`)
          .pipe(catchError(this.handleError)),
      );
      if (!response?.data?.length) return [];
      return response.data[0] as ContaPacienteResumo[];
    } catch {
      return [];
    }
  }

  // ── Ações de supervisor na conta (o back confere o perfil 1848/2027 a cada chamada) ──

  /** Falha vira false: sem certeza de que é supervisor, a tela não mostra as ações. */
  public async isSupervisorConta(): Promise<boolean> {
    try {
      const r = await firstValueFrom(this.httpClient.get<any>(`${this.apiURL}/Atendimento/supervisor-conta`));
      return r?.data?.[0] === true;
    } catch {
      return false;
    }
  }

  public cancelarConta(nrInternoConta: number, motivo: string): Promise<AcaoContaResultado> {
    return this.acaoConta('cancelar-conta', { nrInternoConta, motivo });
  }

  public excluirContaProvisoria(nrInternoConta: number, motivo: MotivoExclusaoConta): Promise<AcaoContaResultado> {
    return this.acaoConta('excluir-conta-provisoria', { nrInternoConta, motivo });
  }

  /** Tira da conta provisória o desconto do cupom do gerenciador; o cupom volta a ficar ativo. */
  public removerCupomConta(nrInternoConta: number): Promise<AcaoContaResultado> {
    return this.acaoConta('remover-cupom-conta', { nrInternoConta });
  }

  /** Cupons usados na conta, que voltam a ficar ativos se ela for excluída. Falha vira lista vazia. */
  public async getCuponsDaConta(nrInternoConta: number): Promise<CupomAplicadoConta[]> {
    try {
      const r = await firstValueFrom(this.httpClient.get<any>(`${this.apiURL}/Atendimento/cupons-da-conta/${nrInternoConta}`));
      return r?.data?.[0] ?? [];
    } catch {
      return [];
    }
  }

  public trocarConvenioConta(
    nrInternoConta: number, cdConvenio: number, cdCategoria: string, motivo: string,
  ): Promise<AcaoContaResultado> {
    return this.acaoConta('trocar-convenio-conta', { nrInternoConta, cdConvenio, cdCategoria, motivo });
  }

  // Aqui a mensagem do back é o que o supervisor precisa ler (403, 409, 422, erro do TASY):
  // o handleError comum trocaria por um texto genérico.
  private async acaoConta(rota: string, corpo: object): Promise<AcaoContaResultado> {
    try {
      const r = await firstValueFrom(this.httpClient.post<any>(`${this.apiURL}/Atendimento/${rota}`, corpo));
      return r?.data?.[0] ?? { nrInternoConta: 0, mensagem: r?.feedback ?? 'Feito.' };
    } catch (e) {
      const err = e as HttpErrorResponse;
      throw new AcaoContaErro(err?.error?.feedback || err?.error?.notifications?.[0]?.message
        || 'Não foi possível concluir a ação. Tente de novo.', err?.status ?? 0);
    }
  }

  public async getAdiantamentosDisponiveis(nrInternoConta: number): Promise<AdiantamentoDisponivel[]> {
    try {
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(`${this.apiURL}/Atendimento/adiantamentos-conta/${nrInternoConta}`)
          .pipe(catchError(this.handleError)),
      );
      if (!response?.data?.length) return [];
      return response.data[0] as AdiantamentoDisponivel[];
    } catch {
      return [];
    }
  }

  public async vincularAdiantamento(request: { nrTitulo: string; nrAdiantamento: number; nmUsuario: string }): Promise<void> {
    await firstValueFrom(
      this.httpClient
        .post<any>(`${this.apiURL}/Atendimento/vincular-adiantamento`, request)
        .pipe(catchError(this.handleError)),
    );
  }

  public async getResumoConclusao(nrAtendimento: number, nrInternoConta: number): Promise<ResumoConclusao | null> {
    try {
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(`${this.apiURL}/Atendimento/resumo-conclusao/${nrAtendimento}?nrInternoConta=${nrInternoConta}`)
          .pipe(
            catchError((err: HttpErrorResponse) => {
              // status 0 = CORS bloqueado por erro no servidor; 500 = erro interno
              if (err.status === 0 || err.status === 500) return of(null);
              return this.handleError(err);
            }),
          ),
      );
      if (!response?.data?.length) return null;
      return response.data[0] as ResumoConclusao;
    } catch {
      return null;
    }
  }

  public async getFichaAtendimento(nrInternoConta: number): Promise<FichaAtendimento | null> {
    try {
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(`${this.apiURL}/Atendimento/ficha-atendimento/${nrInternoConta}`)
          .pipe(catchError(this.handleError)),
      );
      if (!response?.data?.length) return null;
      return response.data[0] as FichaAtendimento;
    } catch {
      return null;
    }
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Erro inesperado. Tente novamente.';

    if (error?.error?.notifications?.length) {
      errorMessage = error.error.notifications[0].message;
    } else if (error?.error?.feedback) {
      errorMessage = error.error.feedback;
    } else if (error?.error?.message) {
      errorMessage = error.error.message;
    }

    console.error('HTTP Error:', error);
    return throwError(() => new Error(errorMessage));
  }

  public async verificarHra(cdPessoaFisica: number): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(`${this.apiURL}/Atendimento/verificar-hra/${cdPessoaFisica}`)
          .pipe(catchError(this.handleError)),
      );
      return response?.data?.[0] === true;
    } catch {
      return true; // Em caso de erro, assume respondido (não bloqueia)
    }
  }

  public async getMeusAtendimentos(nmUsuario: string, cdEstabelecimento: number): Promise<MeuAtendimento[]> {
    try {
      const response = await firstValueFrom(
        this.httpClient
          .get<any>(`${this.apiURL}/Atendimento/meus-atendimentos?nmUsuario=${encodeURIComponent(nmUsuario)}&cdEstabelecimento=${cdEstabelecimento}`)
          .pipe(catchError(this.handleError)),
      );
      if (!response?.data?.length) return [];
      return response.data[0] as MeuAtendimento[];
    } catch {
      return [];
    }
  }

  // tpAgenda é obrigatório: sem ele o back atualiza as duas tabelas de agenda pelo mesmo NR_SEQUENCIA.
  public async atualizarStatusAgenda(nrSeqAgendamento: number, nmUsuario: string, ieStatus: string, tpAgenda: 'EXAM' | 'CONSULTATION'): Promise<void> {
    await firstValueFrom(
      this.httpClient
        .put<any>(`${this.apiURL}/Atendimento/status-agenda?nrSeqAgendamento=${nrSeqAgendamento}&nmUsuario=${encodeURIComponent(nmUsuario)}&ieStatus=${ieStatus}&tpAgenda=${tpAgenda}`, {})
        .pipe(catchError(this.handleError)),
    );
  }

  // ── Avaliação Odontológica Gratuita ──
  public async verificarAvaliacaoOdonto(
    cdPessoaFisica: number,
    cdEstabelecimento: number,
  ): Promise<{
    deveExibir: boolean;
    agendas: { cdAgenda: number; nmMedico: string; qtAC: number }[];
    motivo?: string;
  }> {
    return await firstValueFrom(
      this.httpClient
        .get<any>(`${this.apiURL}/Atendimento/avaliacao-odonto/verificar/${cdPessoaFisica}?cdEstabelecimento=${cdEstabelecimento}`)
        .pipe(
          map((response) => response?.data?.[0] ?? { deveExibir: false, agendas: [] }),
          catchError(() => of({ deveExibir: false, agendas: [], motivo: 'erro' })),
        ),
    );
  }

  public async agendarAvaliacaoOdonto(
    cdPessoaFisica: number,
    cdAgenda: number,
    nmUsuario: string,
    cdConvenio: number | null = null,
    cdCategoria: string | null = null,
    cdEstabelecimento: number = 0,
    cdSetorAtendimento: number | null = null,
  ): Promise<{
    sucesso: boolean;
    nrAtendimento?: number;
    nrPrescricao?: number;
    nmPaciente?: string;
    tipoAtendimento?: string;
    mensagem?: string;
  }> {
    return await firstValueFrom(
      this.httpClient
        .post<any>(`${this.apiURL}/Atendimento/avaliacao-odonto/agendar`, {
          cdPessoaFisica,
          cdAgenda,
          cdConvenio,
          cdCategoria,
          nmUsuario,
          abrirAtendimento: true,
          cdEstabelecimento,
          cdSetorAtendimento,
          ieTipoAtendimento: '8',
        })
        .pipe(
          map((response) => response?.data?.[0] ?? { sucesso: false, mensagem: 'Resposta vazia do servidor.' }),
          catchError((err) => {
            const msg = err?.error?.notifications?.[0]?.message || 'Não foi possível agendar. Tente novamente.';
            return of({ sucesso: false, mensagem: msg });
          }),
        ),
    );
  }
}
